/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Plain ESM JavaScript on purpose (the deepMerge.js pattern): consumed by the
 * client (ArticlePage, seoSchemaPreview), the server (sitemap + seoMeta via
 * tsconfig.server.json include), AND scripts/prerender.mjs — which cannot
 * import TypeScript. Keep it dependency-free.
 */

/**
 * Layout-freshness stamp for kennisbank articles.
 *
 * BUMP THIS DATE whenever a code change alters how published articles are
 * presented to readers — ArticlePage layout, ContentBlockRenderer output,
 * block styling, typography, reading-time display, TOC, author box, etc.
 * CMS edits already touch each article's own `updatedAt`; this stamp covers
 * the programmatic/layout changes that ship via git instead of the CMS.
 *
 * The effective "last edited" date of every article is
 * max(article.updatedAt, ARTICLES_LAYOUT_UPDATED_AT) — used by the visible
 * "Bijgewerkt" date in the article hero, the Article JSON-LD `dateModified`
 * (client and baked HTML), and the sitemap `<lastmod>`.
 *
 * Deliberately a hand-bumped constant, not the build/deploy time: a lastmod
 * that moves on every deploy is noise Google learns to ignore (see the
 * sitemap handler in crawlerFiles.ts). Only bump for changes a reader could see.
 */
export const ARTICLES_LAYOUT_UPDATED_AT = '2026-10-12';

/**
 * Effective last-modified moment for an article: the later of the article's
 * own CMS `updatedAt` (falling back to `createdAt`) and the global layout
 * stamp. Returns whichever original string is the max, so full-precision ISO
 * timestamps survive; date-only output is fine for every consumer.
 *
 * @param {{ createdAt?: string, updatedAt?: string }} article
 * @returns {string}
 */
export function effectiveArticleUpdatedAt(article) {
  const own = article.updatedAt || article.createdAt || '';
  const ownTs = Date.parse(own);
  const layoutTs = Date.parse(ARTICLES_LAYOUT_UPDATED_AT);
  if (Number.isNaN(ownTs)) return ARTICLES_LAYOUT_UPDATED_AT;
  return layoutTs > ownTs ? ARTICLES_LAYOUT_UPDATED_AT : own;
}
